import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CompanyDetails, EntitlementsResponse, IState, ProfileDetails } from '@customTypes/ItemTypes';
import { getLanguageCode, getLanguageCountryCode } from '@helpers/languages';

const hasNavigateInitialValue = {
    deepLinkUrl: '',
    isNavigate: false,
    productName: ''
};
export const initialState: IState = {
    isScreenWidthUnder600Pixel: window.innerWidth <= 600,
    isScreenWidthUnder768Pixel: window.innerWidth <= 767,
    isMobile: window.innerWidth <= 1023,
    isFullPageLoading: false,
    isError: false,
    hasNavigate: hasNavigateInitialValue,
    profileDetails: {
        firstName: '',
        lastName: '',
        phoneNumber: '',
        countryCode: getLanguageCountryCode(),
        accountSettingsSSOLink: '',
        email: '',
        hasSubscription: false,
        hasDashboard: false,
        languagePreference: getLanguageCode(),
        product_subscriptions: [],
        street: '',
        city: '',
        state: '',
        postalCode: '',
        company_name: '',
        title_code: '',
        idaasUUID: '',
        userName: '',
        address1: '',
        address2: '',
        isDnbUser: false
    },
    companyInfo: [],
    entitlements: {
        companyInfo: [],
        credibilityEntitlements: [],
        pla: {
            accepted_tos: '',
            pla_content: undefined
        },
        vboEntitlements: []
    },
    serviceIsDown: false,
    accountSettings: {
        magento: false,
        eb_dashboard: false,
        customer_portal: false
    },
    alreadyLoaded: false,
    allFreemiumProdSubData: [],
    dunsApplication: {},
    activePartnerName: '',
    nextInsuranceTerms: [],
    liliBankingTerms: [],
    lendioTerms: [],
    dunsApplicationProgress: {},
    notifications: [],
    applicationState: {
        isDunsSelected: false,
        isAllProductsVBO: false,
        isCompanyDropDownOnlyAvailable: false,
        isCompanyDropDownWithOutAnyVBOProducts: false,
        deepLinkUrl: '',
    },
    dunsSignupEmail: {}
};

const appSlice = createSlice({
    name: 'app',
    initialState,
    reducers:  {
        setIsMobile: (state: IState, action: PayloadAction<boolean>) => {
            state.isMobile = action.payload;
        },
        setIsScreenWidthUnder600Pixel: (state: IState, action: PayloadAction<boolean>) => {
            state.isScreenWidthUnder600Pixel = action.payload;
        },
        setIsScreenWidthUnder768Pixel: (state: IState, action: PayloadAction<boolean>) => {
            state.isScreenWidthUnder768Pixel = action.payload;
        },
        setIsError: (state: IState, action: PayloadAction<boolean>) => {
            state.isError = action.payload;
        },
        setHasNavigate: (state: IState, action: PayloadAction<{ deepLinkUrl: string; isNavigate: boolean; productName: string; }>) => {
            state.hasNavigate = action.payload;
        },
        setServiceIsDown: (state: IState, action: PayloadAction<boolean>) => {
            state.serviceIsDown = action.payload;
        },
        setIsLoading: (state: IState, action: PayloadAction<boolean>) => {
            state.isFullPageLoading = action.payload;
        },
        setApplicationState: (state: IState, action: PayloadAction<IState['applicationState']>) => {
            state.applicationState = action.payload;
        },
        setAccountSettingsDashboard: (state: IState, action: PayloadAction<boolean>) => {
            state.accountSettings.eb_dashboard = action.payload;
        },
        setAccountSettingsMagento: (state: IState, action: PayloadAction<boolean>) => {
            state.accountSettings.magento = action.payload;
        },
        setAccountSettingsCustomerPortal: (state: IState, action: PayloadAction<boolean>) => {
            state.accountSettings.customer_portal = action.payload;
        },
        setActivePartnerName: (state: IState, action: PayloadAction<string>) => {
            state.activePartnerName = action.payload;
        },
        setNextInsuranceTerms: (state: IState, action: PayloadAction<any[]>) => { // Consider defining a specific type for terms
            state.nextInsuranceTerms = action.payload;
        },
        setLiliBankingTerms: (state: IState, action: PayloadAction<any[]>) => { // Consider defining a specific type for terms
            state.liliBankingTerms = action.payload;
        },
        setLendioTerms: (state: IState, action: PayloadAction<any[]>) => { // Consider defining a specific type for terms
            state.lendioTerms = action.payload;
        },
        setAlreadyLoaded: (state: IState) => {
            state.alreadyLoaded = true;
        },
        clearCompanyInfo: (state: IState) => {
            state.companyInfo = [];
        },
        setCompanyInfo: (state: IState, action: PayloadAction<CompanyDetails[]>) => { // Consider defining a specific type for companyInfo items
            state.companyInfo = action.payload;
        },
        setCompanyInfoIsOpen: (state: IState, action: PayloadAction<boolean>) => {
            state.companyInfoIsOpen = action.payload;
        },
        setSelectedCompanyInfo: (state: IState, action: PayloadAction<any>) => { // Consider defining a specific type
            localStorage.setItem('selectedCompanyInfo', JSON.stringify(action.payload));
            state.selectedCompanyInfo = action.payload;
        },
        saveDunsApplication: (state: IState, action: PayloadAction<object>) => {
            state.dunsApplication = action.payload;
        },
        clearDunsApplication: (state: IState) => {
            state.dunsApplication = {};
        },
        setDunsApplicationProgress: (state: IState, action: PayloadAction<object>) => {
            state.dunsApplicationProgress = action.payload;
        },
        setEntitlements: (state: IState, action: PayloadAction<EntitlementsResponse>) => {
            state.entitlements = action.payload;
        },
        setFreemiumProdDaysLeft: (state: IState, action: PayloadAction<any[]>) => { // Consider defining a specific type
            state.allFreemiumProdSubData = action.payload;
        },
        setProfileDetails: (state: IState, action: PayloadAction<ProfileDetails>) => {
            state.profileDetails = action.payload;
        },
        setNotificationDetails: (state: IState, action: PayloadAction<any[]>) => { // Consider defining a specific type
            state.notifications = action.payload;
        },
        clearNotifications: (state: IState) => {
            state.notifications = [];
        },
        saveDunsSignUpEmail: (state: IState, action: PayloadAction<object>) => {
            state.dunsSignupEmail = action.payload;
        }
    },
});

export const {
    setIsMobile, setIsScreenWidthUnder600Pixel, setIsScreenWidthUnder768Pixel, setIsError, setHasNavigate, setServiceIsDown, setIsLoading, setApplicationState, setAccountSettingsDashboard, setAccountSettingsMagento, setAccountSettingsCustomerPortal, setActivePartnerName, setLiliBankingTerms, setLendioTerms, setNextInsuranceTerms, setAlreadyLoaded, clearCompanyInfo, setCompanyInfo, setCompanyInfoIsOpen, setSelectedCompanyInfo, saveDunsApplication, clearDunsApplication, setDunsApplicationProgress, setEntitlements, setFreemiumProdDaysLeft, setProfileDetails, setNotificationDetails, clearNotifications, saveDunsSignUpEmail
} = appSlice.actions;

export default appSlice.reducer;
